// Imagem colada nos prompts da Central e da /pedro (Pedro, 31/10/2026: «anexar imagem com Ctrl + V ou Win + V (Área de
// transferência do Windows) em todos os inputs de prompt»). O Win+V cola como o Ctrl+V: um evento de colar só. A imagem
// sobe na hora (POST /api/admin/atendimento/anexos) e, ao enviar, o texto ganha uma linha «[imagem anexada: <caminho>]»
// por imagem: a ponte entrega o texto como está e a sessão do Claude lê a imagem pelo caminho. Na página, a linha vira a
// miniatura (TextoComImagens).
import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { ImageIcon, Loader2, X } from "lucide-react";

const MAX_POR_MENSAGEM = 6;
export const LINHA_DE_IMAGEM = /^\[imagem anexada: (\S+\/(\d{8}-\d{6}-[0-9a-f]{8}\.(?:png|jpg|webp)))\]$/;

interface AnexoColado {
  id: string;
  previa: string;
  estado: "enviando" | "pronto" | "erro";
  caminho?: string;
}

function mensagemDoErro(err: unknown): string {
  const m = (err as { data?: { error?: { message?: string } } } | null)?.data?.error?.message;
  return m || "Não foi possível anexar a imagem.";
}

export function useAnexosColados(onErro: (erro: string | null) => void) {
  const [anexos, setAnexos] = useState<AnexoColado[]>([]);
  const vivos = useRef(anexos);
  vivos.current = anexos;
  useEffect(() => () => vivos.current.forEach((a) => URL.revokeObjectURL(a.previa)), []);

  const subir = async (arquivo: File) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const previa = URL.createObjectURL(arquivo);
    setAnexos((a) => [...a, { id, previa, estado: "enviando" }]);
    try {
      const fd = new FormData();
      fd.append("arquivo", arquivo, arquivo.name || "colada.png");
      const r = await customFetch<{ caminho: string }>("/api/admin/atendimento/anexos", { method: "POST", body: fd });
      setAnexos((a) => a.map((x) => (x.id === id ? { ...x, estado: "pronto", caminho: r.caminho } : x)));
    } catch (err) {
      setAnexos((a) => a.map((x) => (x.id === id ? { ...x, estado: "erro" } : x)));
      onErro(mensagemDoErro(err));
    }
  };

  const colar = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const dt = e.clipboardData;
    // Com texto junto (célula de planilha, trecho de página), cola o texto, como sempre.
    if (dt.getData("text/plain").trim()) return;
    const imagens = [...dt.items]
      .filter((i) => i.kind === "file" && i.type.startsWith("image/"))
      .map((i) => i.getAsFile())
      .filter((f): f is File => !!f);
    if (imagens.length === 0) return;
    e.preventDefault();
    const cabem = MAX_POR_MENSAGEM - vivos.current.length;
    if (imagens.length > cabem) onErro(`Até ${MAX_POR_MENSAGEM} imagens por mensagem.`);
    else onErro(null);
    imagens.slice(0, Math.max(0, cabem)).forEach((f) => void subir(f));
  };

  const tirar = (id: string) =>
    setAnexos((a) => {
      const x = a.find((y) => y.id === id);
      if (x) URL.revokeObjectURL(x.previa);
      return a.filter((y) => y.id !== id);
    });
  const limpar = () => {
    vivos.current.forEach((a) => URL.revokeObjectURL(a.previa));
    setAnexos([]);
  };
  const prontos = anexos.filter((a) => a.estado === "pronto" && a.caminho);
  return {
    anexos,
    colar,
    tirar,
    limpar,
    /** Ainda subindo: o Enviar espera. */
    subindo: anexos.some((a) => a.estado === "enviando"),
    /** Há imagem pronta: dá para enviar mesmo sem texto. */
    temImagem: prontos.length > 0,
    /** O texto do prompt com uma linha por imagem pronta. */
    junto: (texto: string) => [texto.trim(), ...prontos.map((a) => `[imagem anexada: ${a.caminho}]`)].filter(Boolean).join("\n"),
  };
}

/** As miniaturas das imagens coladas, embaixo do campo, antes de enviar. */
export function MiniaturasColadas({ anexos, onTirar }: { anexos: AnexoColado[]; onTirar: (id: string) => void }) {
  if (anexos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="anexos-colados">
      {anexos.map((a) => (
        <div
          key={a.id}
          className={`relative h-16 w-16 overflow-hidden rounded-lg border bg-muted ${a.estado === "erro" ? "border-destructive" : ""}`}
          data-testid="anexo-colado"
          data-estado={a.estado}
        >
          <img src={a.previa} alt="Imagem colada" className={`h-full w-full object-cover ${a.estado === "enviando" ? "opacity-50" : ""}`} />
          {a.estado === "enviando" && <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-foreground" />}
          <button
            type="button"
            onClick={() => onTirar(a.id)}
            className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            aria-label="Tirar a imagem"
            data-testid="anexo-colado-tirar"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

function ImagemAnexada({ nome }: { nome: string }) {
  const q = useQuery({
    queryKey: ["central-anexo", nome],
    queryFn: async () => URL.createObjectURL(await customFetch<Blob>(`/api/admin/atendimento/anexos/${nome}`, { responseType: "blob" })),
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
  });
  if (q.isError) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground" data-testid="imagem-anexada-erro">
        <ImageIcon className="h-3.5 w-3.5" /> imagem
      </span>
    );
  }
  if (!q.data) return <span className="block h-24 w-32 animate-pulse rounded-lg bg-muted" />;
  return (
    <a href={q.data} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="block" data-testid="imagem-anexada">
      <img src={q.data} alt="Imagem anexada" className="max-h-56 max-w-full rounded-lg border object-contain" />
    </a>
  );
}

/** O texto de um prompt com as linhas «[imagem anexada: …]» trocadas pelas imagens. */
export function TextoComImagens({ texto }: { texto: string }) {
  const linhas = texto.split("\n");
  const nomes = linhas.map((l) => l.trim().match(LINHA_DE_IMAGEM)?.[2] ?? null);
  if (!nomes.some(Boolean)) return <>{texto}</>;
  const resto = linhas.filter((_, i) => !nomes[i]).join("\n").trim();
  return (
    <>
      {resto}
      <span className={`flex flex-wrap gap-2 ${resto ? "mt-2" : ""}`}>
        {nomes.filter((n): n is string => !!n).map((n) => (
          <ImagemAnexada key={n} nome={n} />
        ))}
      </span>
    </>
  );
}

/** O texto sem as linhas de imagem, para as prévias de uma ou duas linhas («📷 imagem» no lugar). */
export function semLinhasDeImagem(texto: string): string {
  const linhas = texto.split("\n");
  const n = linhas.filter((l) => LINHA_DE_IMAGEM.test(l.trim())).length;
  if (n === 0) return texto;
  const resto = linhas.filter((l) => !LINHA_DE_IMAGEM.test(l.trim())).join("\n").trim();
  const rotulo = n === 1 ? "📷 imagem" : `📷 ${n} imagens`;
  return resto ? `${resto} · ${rotulo}` : rotulo;
}
